import type { ApiClient } from '../../api/client.js';
import { initialPairingApi } from '../../api/initial-pairing.js';
import { BleSetupError, DeviceBleTransport, type BluetoothAdapter } from '../../device-ble/transport.js';
import { provisionOwnedDevice } from '../../device-ble/provision.js';
import { provisionInitialDevice } from '../../device-ble/initial-provision.js';
import { SUPPORTED_USB_FILTERS } from '../../device-usb/index.js';
import { getMobileBridge } from '../../platform/mobile-bridge.js';
import { createDeviceSync, type BleDriver, type DeviceSync, type Provisioner, type SyncLifecycle, type UsbProbe } from './device-sync.js';

type BluetoothNavigator = Navigator & { bluetooth?: BluetoothAdapter };
interface SerialLike { getPorts?(): Promise<Array<{ getInfo(): { usbVendorId?: number; usbProductId?: number } }>> }
type SerialNavigator = Navigator & { serial?: SerialLike };

/** The native shell may take this long to stop a scan before a new one starts. */
const NATIVE_CANCEL_MS = 2000;

export function createBleDriver(): BleDriver | null {
  // The native shell's adapter replaces Web Bluetooth, and the Mac chooser bridge never applies inside it.
  const mobile = getMobileBridge();
  const bluetooth: BluetoothAdapter | undefined = mobile?.bluetooth ?? (navigator as BluetoothNavigator).bluetooth;
  if (!bluetooth) return null;
  const bridge = mobile ? undefined : window.asisDesktop?.bluetooth;
  const nativeCancel = mobile?.cancelBluetooth?.bind(mobile);
  let cleanup: Promise<void> | null = null;
  return {
    async cancel() {
      if (!nativeCancel) return;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const task: Promise<void> = Promise.race([
        Promise.resolve().then(nativeCancel),
        new Promise<void>(resolve => { timer = setTimeout(resolve, NATIVE_CANCEL_MS); }),
      ]).catch(() => undefined).then(() => { clearTimeout(timer); if (cleanup === task) cleanup = null; });
      cleanup = task;
      await task;
    },
    // Web Bluetooth: no await before transport.connect, requestDevice must start inside the click.
    // A native scan that is still being cancelled is awaited first, bounded, so two scans never overlap.
    async connect(signal, hooks) {
      if (cleanup) { await cleanup; if (signal.aborted) throw new BleSetupError('cancelled'); }
      const transport = new DeviceBleTransport(bluetooth, bridge, hooks.onDisconnected);
      const stop = bridge ? bridge.onDevices(event => hooks.onChoices(event.devices.map(device => ({
        deviceId: device.deviceId, deviceName: device.deviceName, select: () => bridge.select(event.requestId, device.deviceId),
      })))) : undefined;
      try { return { info: await transport.connect(signal), transport }; }
      finally { stop?.(); }
    },
  };
}

/** Only lists ports the user already authorised; opening or requesting one stays in the USB diagnostics. */
export function createUsbProbe(connectedDeviceId: () => string | null): UsbProbe | null {
  if (getMobileBridge()) return null; // The native shells have no USB serial; its diagnostics are not mounted either.
  const serial = (navigator as SerialNavigator).serial;
  if (!serial) return null;
  return {
    async detect() {
      const ports = typeof serial.getPorts === 'function' ? await serial.getPorts() : [];
      const supported = ports.filter(port => {
        const info = port.getInfo();
        return SUPPORTED_USB_FILTERS.some(filter => filter.usbVendorId === info.usbVendorId && filter.usbProductId === info.usbProductId);
      });
      return { ports: supported.length, connectedDeviceId: connectedDeviceId() };
    },
  };
}

/** Existing BLE flows, untouched: this only adapts their arguments. */
export function createProvisioner(api: ApiClient): Provisioner {
  return {
    owned: call => provisionOwnedDevice({ api, transport: call.link.transport, info: call.info, network: call.network, signal: call.signal, onProgress: call.onProgress }),
    initial: call => {
      // Account networks only reach a unit that proves the code on its screen; add() refuses them for a factory label.
      if (!('code' in call.possession) && ('networkId' in call.network || 'networkIds' in call.network)) return Promise.reject(new BleSetupError('invalid_info'));
      return provisionInitialDevice({ api: initialPairingApi(api), transport: call.link.transport, info: call.info, network: call.network, possession: call.possession, signal: call.signal, onProgress: call.onProgress });
    },
  };
}

/** Native shells report foreground changes; elsewhere the page is always "in front". */
export function createLifecycle(): SyncLifecycle | null {
  const mobile = getMobileBridge();
  return mobile ? { subscribe: listener => mobile.onAppStateChange(listener) } : null;
}

export function createProductionSync(api: ApiClient, connectedUsbId: () => string | null, options: { usb?: boolean } = {}): DeviceSync {
  return createDeviceSync({
    listAccount: async signal => (await api.listDevices(signal)).devices,
    ble: createBleDriver(), usb: options.usb === false ? null : createUsbProbe(connectedUsbId), provisioner: createProvisioner(api), lifecycle: createLifecycle(),
  });
}
